import { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import '../index.css';

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? 'https://muki684.com' : 'https://muki684.com',
});

function catLabel(c) {
  return c.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function fmtReviewDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function StarInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="star-input">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button"
          className={`star-btn${n <= (hover || value) ? ' filled' : ''}`}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}>
          <i className={`fa-${n <= (hover || value) ? 'solid' : 'regular'} fa-star`} />
        </button>
      ))}
    </div>
  );
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function CountUp({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        const start = performance.now();
        const dur = 1600;
        const tick = now => {
          const t = Math.min((now - start) / dur, 1);
          setCount(Math.round((1 - Math.pow(1 - t, 3)) * target));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.unobserve(e.target);
      }
    }, { threshold: 0.5 });
    const el = ref.current;
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

const CUP_LAYOUTS = [
  { w: 340, style: { top: '-30px',    right: '-50px',  transform: 'rotate(12deg)'  } },
  { w: 200, style: { bottom: '-20px', left: '-30px',   transform: 'rotate(-8deg)'  } },
  { w: 140, style: { top: '38%',      right: '17%',    transform: 'rotate(22deg)'  } },
];

function LogoWatermark({ side = 'right' }) {
  const pos = {
    right:  { right: '-60px',  top: '50%', transform: 'translateY(-50%)' },
    left:   { left:  '-60px',  top: '50%', transform: 'translateY(-50%)' },
    center: { left:  '50%',    top: '50%', transform: 'translate(-50%, -50%)' },
  }[side];
  return (
    <div className="logo-watermark" aria-hidden="true">
      <img src="/logos/logo_02_white_on_white.png" alt="" style={pos} />
    </div>
  );
}

function CoffeeDecor({ count = 3, dark = false }) {
  const stroke = dark ? 'rgba(255,255,255,0.13)' : 'rgba(63,35,5,0.08)';
  const sw = 2.5;
  return (
    <div className="tri-decor" aria-hidden="true">
      {CUP_LAYOUTS.slice(0, count).map((t, i) => (
        <svg key={i} width={t.w} height={Math.round(t.w * 1.25)}
          viewBox="0 0 100 125" fill="none" xmlns="http://www.w3.org/2000/svg"
          style={{ position: 'absolute', pointerEvents: 'none', ...t.style }}>
          {/* steam */}
          <path d="M33,30 C31,23 35,18 33,11" stroke={stroke} strokeWidth={sw - 0.5} strokeLinecap="round"/>
          <path d="M50,28 C48,20 52,15 50,7"  stroke={stroke} strokeWidth={sw - 0.5} strokeLinecap="round"/>
          <path d="M67,30 C65,23 69,18 67,11" stroke={stroke} strokeWidth={sw - 0.5} strokeLinecap="round"/>
          {/* cup body */}
          <path d="M15,37 L22,87 L78,87 L85,37 Z" stroke={stroke} strokeWidth={sw} strokeLinejoin="round"/>
          {/* handle */}
          <path d="M85,52 C108,52 108,77 85,77" stroke={stroke} strokeWidth={sw}/>
          {/* saucer */}
          <ellipse cx="50" cy="94" rx="44" ry="7" stroke={stroke} strokeWidth={sw}/>
        </svg>
      ))}
    </div>
  );
}

const ToastCtx = createContext(() => {});
const CartCtx  = createContext(null);
function useCart() { return useContext(CartCtx); }

/* ================================================================
   NAVBAR
================================================================ */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, setDrawerOpen } = useCart();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav id="navbar" className={scrolled ? 'scrolled' : ''}>
      <div className="container">
        <div className="nav-inner">
          <a href="/" className="nav-logo">
            <img
              src={scrolled ? '/logos/logo_01_green_on_white.png' : '/logos/logo_05_white_on_black.png'}
              alt="Muki 684 Coffee"
              className="nav-logo-img"
            />
          </a>
          <ul className={`nav-links${open ? ' open' : ''}`}>
            {['gallery','menu','about','reviews','reservations','contact'].map(s => (
              <li key={s}><a href={`#${s}`} onClick={() => setOpen(false)}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </a></li>
            ))}
          </ul>
          <a href="#reservations"
            className={`btn btn-primary nav-cta${open ? ' open' : ''}`}
            onClick={() => setOpen(false)}>
            Reserve
          </a>
          <button className="nav-cart" aria-label="Cart" onClick={() => setDrawerOpen(true)}>
            <i className="fa-solid fa-bag-shopping" />
            {count > 0 && <span className="cart-badge">{count}</span>}
          </button>
          <button className="nav-toggle" aria-label="Menu" onClick={() => setOpen(o => !o)}>
            <span /><span /><span />
          </button>
        </div>
      </div>
    </nav>
  );
}

/* ================================================================
   HERO
================================================================ */
function Hero() {
  return (
    <section id="hero">
      <CoffeeDecor count={3} dark />
      <div className="container">
        <div className="hero-inner">
          <div>
            <h1 className="hero-title">
              Where every<br />cup<em>tells a story.</em>
            </h1>
          </div>
          <div className="hero-right">
            <p className="hero-sub">
              Handcrafted from single-origin beans. Roasted in-house
              every week. Served by people who genuinely care about
              what's in your cup.
            </p>
            <div className="hero-actions">
              <a href="#menu" className="btn btn-primary">Order Online</a>
              <a href="#reservations" className="btn btn-outline-light">Book a Table</a>
            </div>
          </div>
        </div>
      </div>
      <div className="hero-scroll">
        <span>Scroll</span>
        <i className="fa-solid fa-arrow-down" />
      </div>
    </section>
  );
}

/* ================================================================
   ABOUT
================================================================ */
function About() {
  const [ref, visible] = useReveal();
  return (
    <section id="about">
      <CoffeeDecor count={2} />
      <LogoWatermark side="right" />
      <div className="container">
        <div className={`about-grid reveal${visible ? ' visible' : ''}`} ref={ref}>
          <div className="about-image-wrap">
            <img
              src="https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=800&q=80&auto=format"
              alt="Barista at work"
              className="about-img"
            />
            <div className="about-year">
              <span className="big">1</span>
              <span className="label">Year open</span>
            </div>
          </div>

          <div>
            <div className="section-kicker">Our Story</div>
            <h2 className="section-title">
              Small roastery,<br /><em>big obsession.</em>
            </h2>
            <div className="rule" />
            <p className="section-sub">
              We started in 2026 with a hand-me-down roaster and a single-origin
              Ethiopian natural. Twelve years later we still get the same feeling
              opening a fresh bag — that's what we want you to taste.
            </p>

            <div className="about-stats">
              {[{t:50000,s:'+',l:'Cups served'},{t:18,s:'',l:'Origins'},{t:97,s:'%',l:'Regulars'}].map(x => (
                <div key={x.l} className="stat-item">
                  <div className="stat-num"><CountUp target={x.t} suffix={x.s} /></div>
                  <div className="stat-label">{x.l}</div>
                </div>
              ))}
            </div>

            <div className="about-features">
              {[
                { icon:'fa-seedling', title:'Direct trade', desc:'We visit farms, not brokers. Better coffee, fairer pay.' },
                { icon:'fa-fire',     title:'Weekly roasts', desc:'Roasted on site so nothing sits longer than seven days.' },
                { icon:'fa-award',    title:'Competition team', desc:'Our baristas train, compete, and bring new techniques back to the bar.' },
              ].map(f => (
                <div key={f.icon} className="feature-item">
                  <i className={`fa-solid ${f.icon} feature-icon`} />
                  <div className="feature-text"><h4>{f.title}</h4><p>{f.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   GALLERY
================================================================ */
function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  const photo = photos[index];

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowLeft')  onPrev();
      if (e.key === 'ArrowRight') onNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  if (!photo) return null;
  const src = photo.url.startsWith('http') ? photo.url : `https://muki684.com${photo.url}`;

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose} aria-label="Close">
        <i className="fa-solid fa-xmark" />
      </button>

      {photos.length > 1 && (
        <>
          <button className="lightbox-arrow lightbox-prev"
            onClick={e => { e.stopPropagation(); onPrev(); }} aria-label="Previous">
            <i className="fa-solid fa-chevron-left" />
          </button>
          <button className="lightbox-arrow lightbox-next"
            onClick={e => { e.stopPropagation(); onNext(); }} aria-label="Next">
            <i className="fa-solid fa-chevron-right" />
          </button>
        </>
      )}

      <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
        <img src={src} alt={photo.caption || ''} className="lightbox-img" />
        {photo.caption && <p className="lightbox-caption">{photo.caption}</p>}
      </div>

      <span className="lightbox-counter">{index + 1} / {photos.length}</span>
    </div>
  );
}

function AlbumFrame({ album, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="album-overlay" onClick={onClose}>
      <div className="album-modal" onClick={e => e.stopPropagation()}>
        <div className="album-modal-header">
          <div className="album-modal-title">
            <i className="fa-solid fa-images" />
            <span>{album.name}</span>
          </div>
          <div className="album-modal-actions">
            <a href={album.url} target="_blank" rel="noreferrer" className="album-ext-link"
              title="Open in new tab">
              <i className="fa-solid fa-arrow-up-right-from-square" />
              <span>Open original</span>
            </a>
            <button className="lightbox-close album-close-btn" onClick={onClose} aria-label="Close">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
        <div className="album-iframe-wrap">
          <iframe
            src={album.url}
            title={album.name}
            className="album-iframe"
            allow="fullscreen"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
          <div className="album-iframe-fallback">
            <i className="fa-solid fa-photo-film" />
            <p>This album can't be embedded directly.</p>
            <a href={album.url} target="_blank" rel="noreferrer" className="btn btn-primary">
              Open Album <i className="fa-solid fa-arrow-up-right-from-square" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Gallery() {
  const [photos, setPhotos]     = useState([]);
  const [albums, setAlbums]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [lb, setLb]             = useState({ open: false, index: 0 });
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [ref, visible]          = useReveal();

  useEffect(() => {
    Promise.all([
      api.get('/api/gallery'),
      api.get('/api/gallery/albums'),
    ]).then(([photosRes, albumsRes]) => {
      setPhotos(photosRes.data);
      setAlbums(albumsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const open  = (i) => setLb({ open: true, index: i });
  const close = () => setLb(s => ({ ...s, open: false }));
  const prev  = () => setLb(s => ({ ...s, index: (s.index - 1 + photos.length) % photos.length }));
  const next  = () => setLb(s => ({ ...s, index: (s.index + 1) % photos.length }));

  if (!loading && photos.length === 0 && albums.length === 0) return null;

  return (
    <section id="gallery">
      <div className="container">
        <div className={`reveal${visible ? ' visible' : ''}`} ref={ref}>
          <div className="section-kicker">Gallery</div>
          <h2 className="section-title">Our <em>space.</em></h2>
          <div className="rule" />
        </div>

        {loading
          ? <div className="gallery-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
          : (
            <div className={`gallery-grid${photos.length === 0 && albums.length > 0 ? ' gallery-albums-only' : ''}`}>
              {albums.map(album => (
                <button key={`album-${album.id}`} className="gallery-item gallery-album-card"
                  onClick={() => setActiveAlbum(album)}
                  aria-label={`Open album: ${album.name}`}>
                  <div className="album-mini-wrap">
                    <iframe
                      src={album.url}
                      title={album.name}
                      className="album-mini-iframe"
                      tabIndex={-1}
                      sandbox="allow-scripts allow-same-origin"
                      loading="lazy"
                    />
                  </div>
                  <div className="album-mini-overlay">
                    <div className="album-mini-info">
                      <i className="fa-solid fa-images" />
                      <strong>{album.name}</strong>
                    </div>
                    <span className="album-mini-cta">View full album →</span>
                  </div>
                </button>
              ))}
              {photos.map((p, i) => {
                const src = p.url.startsWith('http') ? p.url : `https://muki684.com${p.url}`;
                return (
                  <button key={p.id} className="gallery-item" onClick={() => open(i)}
                    aria-label={p.caption || `Photo ${i + 1}`}>
                    <img src={src} alt={p.caption || ''} loading="lazy" />
                    {p.caption && <span className="gallery-caption">{p.caption}</span>}
                  </button>
                );
              })}
            </div>
          )
        }
      </div>

      {lb.open && (
        <Lightbox photos={photos} index={lb.index}
          onClose={close} onPrev={prev} onNext={next} />
      )}

      {activeAlbum && (
        <AlbumFrame album={activeAlbum} onClose={() => setActiveAlbum(null)} />
      )}
    </section>
  );
}

/* ================================================================
   MENU
================================================================ */
function MenuCard({ item }) {
  const [ref, visible] = useReveal();
  const { addItem } = useCart();
  return (
    <div ref={ref} className={`menu-card reveal${visible ? ' visible' : ''}`}>
      <span className={`card-cat cat-${item.category}`}>{catLabel(item.category)}</span>
      <h3 className="card-name">{item.name}</h3>
      <p className="card-desc">{item.description}</p>
      <div className="card-footer">
        <span className="card-price">RWF{parseFloat(item.price).toFixed(2)}</span>
        <button className="card-add" onClick={() => addItem(item)} title="Add to order">
          <i className="fa-solid fa-plus" />
        </button>
      </div>
    </div>
  );
}

const MENU_PAGE = 6;

function MenuPdfModal({ onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="album-overlay" onClick={onClose}>
      <div className="album-modal" onClick={e => e.stopPropagation()}>
        <div className="album-modal-header">
          <div className="album-modal-title">
            <i className="fa-solid fa-file-pdf" />
            <span>Menu</span>
          </div>
          <div className="album-modal-actions">
            <a href="https://muki684.com/api/menu-pdf/view" target="_blank" rel="noreferrer"
              className="album-ext-link" title="Open in new tab">
              <i className="fa-solid fa-arrow-up-right-from-square" />
              <span>Open in new tab</span>
            </a>
            <button className="lightbox-close album-close-btn" onClick={onClose} aria-label="Close">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
        <div className="album-iframe-wrap">
          <iframe
            src="https://muki684.com/api/menu-pdf/view"
            title="Menu PDF"
            className="album-iframe"
            allow="fullscreen"
          />
        </div>
      </div>
    </div>
  );
}

function Menu() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [menuPdf, setMenuPdf] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const cats = [
    'all','breakfast','skewers','soup','healthy','sandwiches','lunch','sides',
    'dessert','hot_drinks','cold_drinks','mocktails','smoothies','juices',
    'tea','milkshakes','lemonades','ice_cream',
  ];

  useEffect(() => {
    api.get('/api/menu')
      .then(r => { setItems(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get('/api/menu-pdf')
      .then(r => setMenuPdf(r.data))
      .catch(() => {});
  }, []);

  const filtered = filter === 'all' ? items : items.filter(i => i.category === filter);
  const visible  = showAll ? filtered : filtered.slice(0, MENU_PAGE);
  const hidden   = filtered.length - visible.length;

  function changeFilter(c) { setFilter(c); setShowAll(false); }

  return (
    <section id="menu">
      <CoffeeDecor count={2} />
      <div className="container">
        <div className="menu-header">
          <div>
            <div className="section-kicker">Menu & Order</div>
            <h2 className="section-title">Pick your <em>cup.</em></h2>
            {menuPdf?.url && (
              <button className="btn btn-outline menu-pdf-btn" onClick={() => setPdfOpen(true)}>
                <i className="fa-solid fa-file-pdf" /> View Menu PDF
              </button>
            )}
          </div>
          <div className="menu-filters">
            {cats.map(c => (
              <button key={c}
                className={`filter-btn${filter === c ? ' active' : ''}`}
                onClick={() => changeFilter(c)}>
                {c === 'all' ? 'All' : catLabel(c)}
              </button>
            ))}
          </div>
        </div>
        <div className="menu-grid">
          {loading
            ? <div className="menu-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
            : visible.map(item => <MenuCard key={item.id} item={item} />)
          }
        </div>
        {!loading && filtered.length > MENU_PAGE && (
          <div className="reviews-more">
            <button className="btn btn-outline" onClick={() => setShowAll(s => !s)}>
              {showAll
                ? <><i className="fa-solid fa-chevron-up" /> Show less</>
                : <><i className="fa-solid fa-chevron-down" /> Show {hidden} more item{hidden !== 1 ? 's' : ''}</>}
            </button>
          </div>
        )}
      </div>
      {pdfOpen && <MenuPdfModal onClose={() => setPdfOpen(false)} />}
    </section>
  );
}

/* ================================================================
   CART DRAWER
================================================================ */
function CartDrawer() {
  const { items, updateQty, removeItem, total, count, drawerOpen, setDrawerOpen, setCheckoutOpen } = useCart();

  return (
    <>
      {drawerOpen && <div className="cart-overlay" onClick={() => setDrawerOpen(false)} />}
      <div className={`cart-drawer${drawerOpen ? ' open' : ''}`} role="dialog" aria-label="Your order">
        <div className="cart-drawer-header">
          <h3>Your Order <span>{count} item{count !== 1 ? 's' : ''}</span></h3>
          <button className="cart-close-btn" onClick={() => setDrawerOpen(false)} aria-label="Close cart">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="cart-drawer-body">
          {items.length === 0
            ? (
              <div className="cart-empty">
                <i className="fa-solid fa-mug-hot" />
                <p>No items yet — add from the menu</p>
              </div>
            )
            : items.map(item => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-info">
                  <span className={`card-cat cat-${item.category}`}>{catLabel(item.category)}</span>
                  <strong>{item.name}</strong>
                </div>
                <div className="cart-item-right">
                  <button className="qty-btn" onClick={() => updateQty(item.id, -1)} aria-label="Decrease">−</button>
                  <span className="qty-num">{item.qty}</span>
                  <button className="qty-btn" onClick={() => updateQty(item.id, +1)} aria-label="Increase">+</button>
                  <span className="cart-item-price">${(parseFloat(item.price) * item.qty).toFixed(2)}</span>
                  <button className="cart-remove" onClick={() => removeItem(item.id)} aria-label="Remove">
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              </div>
            ))
          }
        </div>

        {items.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-total">
              <span>Total</span>
              <strong>${total.toFixed(2)}</strong>
            </div>
            <button
              className="btn btn-primary cart-checkout-btn"
              onClick={() => { setDrawerOpen(false); setCheckoutOpen(true); }}>
              Place Order →
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/* ================================================================
   CHECKOUT MODAL
================================================================ */
function CheckoutModal() {
  const { items, total, clearCart, checkoutOpen, setCheckoutOpen } = useCart();
  const addToast = useContext(ToastCtx);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const form = Object.fromEntries(new FormData(e.target));
    try {
      await api.post('/api/orders', {
        customer_name:  form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        notes:          form.notes,
        items: items.map(i => ({
          menu_item_id: i.id,
          name:         i.name,
          price:        i.price,
          quantity:     i.qty,
        })),
      });
      addToast("Order placed! We'll have it ready soon.", 'success');
      clearCart();
      setCheckoutOpen(false);
    } catch (err) {
      addToast(err.response?.data?.error || 'Order failed, please try again.', 'error');
    }
    setLoading(false);
  }

  if (!checkoutOpen) return null;

  return (
    <div className="checkout-overlay"
      onClick={e => { if (e.target === e.currentTarget) setCheckoutOpen(false); }}>
      <div className="checkout-modal">
        <div className="checkout-header">
          <h3>Complete Your Order</h3>
          <button onClick={() => setCheckoutOpen(false)} aria-label="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="checkout-summary">
          {items.map(i => (
            <div key={i.id} className="checkout-row">
              <span>{i.name} ×{i.qty}</span>
              <span>${(parseFloat(i.price) * i.qty).toFixed(2)}</span>
            </div>
          ))}
          <div className="checkout-total-row">
            <span>Total</span>
            <strong>${total.toFixed(2)}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input name="customer_name" required placeholder="Jane Smith" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input name="customer_email" type="email" placeholder="jane@example.com" />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="customer_phone" type="tel" placeholder="+1 (555) 000-0000" />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" rows={3} placeholder="Allergies, special requests, pickup time..." />
          </div>
          <button type="submit" className="btn btn-primary form-submit" disabled={loading}>
            {loading
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Placing order…</>
              : 'Confirm Order'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ================================================================
   REVIEWS
================================================================ */
function Reviews() {
  const addToast = useContext(ToastCtx);
  const [reviews, setReviews]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAll, setShowAll]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [rating, setRating]       = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [ref, visible] = useReveal();

  useEffect(() => {
    api.get('/api/reviews')
      .then(r => { setReviews(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function openModal()  { setModalOpen(true);  setRating(0); }
  function closeModal() { setModalOpen(false); setRating(0); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (rating === 0) { addToast('Please select a star rating.', 'error'); return; }
    setSubmitting(true);
    const form = Object.fromEntries(new FormData(e.target));
    try {
      await api.post('/api/reviews', { ...form, rating });
      addToast('Thanks! Your review will appear after approval.', 'success');
      closeModal();
    } catch (err) {
      addToast(err.response?.data?.error || 'Submission failed.', 'error');
    }
    setSubmitting(false);
  }

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <section id="reviews">
      <CoffeeDecor count={3} />
      <LogoWatermark side="left" />
      <div className="container">
        <div className={`reveal${visible ? ' visible' : ''}`} ref={ref}>
          <div className="section-kicker">Reviews</div>
          <div className="reviews-heading">
            <div>
              <h2 className="section-title">What our <em>regulars say.</em></h2>
              {avg && (
                <div className="reviews-avg">
                  <span className="avg-score">{avg}</span>
                  <div>
                    <div className="review-stars">
                      {[1,2,3,4,5].map(n => (
                        <i key={n} className={`fa-${n <= Math.round(avg) ? 'solid' : 'regular'} fa-star`} />
                      ))}
                    </div>
                    <span className="avg-label">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-outline reviews-write-btn" onClick={openModal}>
              <i className="fa-solid fa-pen-to-square" /> Write a Review
            </button>
          </div>
          <div className="rule" />
        </div>

        {loading && (
          <div className="reviews-loading"><i className="fa-solid fa-circle-notch fa-spin" /></div>
        )}

        {!loading && reviews.length > 0 && (
          <>
            <div className="reviews-list">
              {(showAll ? reviews : reviews.slice(0, 3)).map(r => (
                <div key={r.id} className="review-card reveal visible">
                  <div className="review-card-top">
                    <div className="review-stars">
                      {[1,2,3,4,5].map(n => (
                        <i key={n} className={`fa-${n <= r.rating ? 'solid' : 'regular'} fa-star`} />
                      ))}
                    </div>
                    <span className="review-date">{fmtReviewDate(r.created_at)}</span>
                  </div>
                  <p className="review-text">"{r.message}"</p>
                  <strong className="review-name">{r.customer_name}</strong>
                </div>
              ))}
            </div>
            {reviews.length > 3 && (
              <div className="reviews-more">
                <button className="btn btn-outline" onClick={() => setShowAll(s => !s)}>
                  {showAll
                    ? <><i className="fa-solid fa-chevron-up" /> Show less</>
                    : <><i className="fa-solid fa-chevron-down" /> Show {reviews.length - 3} more review{reviews.length - 3 !== 1 ? 's' : ''}</>}
                </button>
              </div>
            )}
          </>
        )}

        {!loading && reviews.length === 0 && (
          <div className="reviews-empty">
            <i className="fa-regular fa-star" />
            <p>No reviews yet — be the first to share your experience.</p>
            <button className="btn btn-primary" onClick={openModal}>Write a Review</button>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="checkout-overlay"
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="checkout-modal">
            <div className="checkout-header">
              <h3>Leave a Review</h3>
              <button onClick={closeModal} aria-label="Close">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Your Rating *</label>
                <StarInput value={rating} onChange={setRating} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input name="customer_name" required placeholder="Jane Smith" autoFocus />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input name="customer_email" type="email" placeholder="jane@example.com" />
                </div>
              </div>
              <div className="form-group">
                <label>Your Review *</label>
                <textarea name="message" required rows={4} placeholder="Tell us about your experience..." />
              </div>
              <button type="submit" className="btn btn-primary form-submit" disabled={submitting}>
                {submitting
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Submitting…</>
                  : 'Submit Review'}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

/* ================================================================
   RESERVATIONS
================================================================ */
function Reservations() {
  const addToast = useContext(ToastCtx);
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  async function handleSubmit(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/api/reservations', Object.fromEntries(new FormData(e.target)));
      addToast('Reservation received — see you soon.', 'success');
      e.target.reset();
    } catch (err) {
      addToast(err.response?.data?.error || 'Something went wrong.', 'error');
    }
    setLoading(false);
  }

  return (
    <section id="reservations">
      <CoffeeDecor count={2} />
      <LogoWatermark side="center" />
      <div className="container">
        <div className="reservations-grid">
          <div>
            <div className="section-kicker">Book a table</div>
            <h2 className="section-title">Come sit<br /><em>with us.</em></h2>
            <div className="rule" />
            <p className="section-sub">
              We hold a few tables back each day for walk-ins, but booking
              ahead means you choose the spot.
            </p>
            <div className="res-info-items">
              {[
                { icon:'fa-clock',        title:'Hours',    text:'Mon–Fri  7:00 – 21:00\nSat–Sun  8:00 – 22:00' },
                { icon:'fa-location-dot', title:'Find us',  text:'Kacyiru, below US Embassy, Stone Road\nKG 684 St, Irebe Compound' },
                { icon:'fa-phone',        title:'Phone',    text:'0782 341 304 / 0788 546 357' },
              ].map(i => (
                <div key={i.icon} className="res-info-item">
                  <i className={`fa-solid ${i.icon} res-info-icon`} />
                  <div className="res-info-text"><h4>{i.title}</h4><p>{i.text}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input name="name" required placeholder="Jane Smith" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input name="email" type="email" required placeholder="jane@example.com" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input name="date" type="date" required min={today} />
                </div>
                <div className="form-group">
                  <label>Time *</label>
                  <input name="time" type="time" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" type="tel" placeholder="+1 (555) 000-0000" />
                </div>
                <div className="form-group">
                  <label>Guests</label>
                  <select name="guests" defaultValue="2">
                    {[1,2,3,4,5,6,7,8].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'person' : 'people'}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea name="message" placeholder="Allergies, special occasions, seating preferences..." />
              </div>
              <button type="submit" className="btn btn-primary form-submit" disabled={loading}>
                {loading
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Booking…</>
                  : 'Request Reservation'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   CONTACT
================================================================ */
function Contact() {
  const addToast = useContext(ToastCtx);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/api/contact', Object.fromEntries(new FormData(e.target)));
      addToast("Got it — we'll reply shortly.", 'success');
      e.target.reset();
    } catch (err) {
      addToast(err.response?.data?.error || 'Something went wrong.', 'error');
    }
    setLoading(false);
  }

  return (
    <section id="contact">
      <CoffeeDecor count={3} />
      <div className="container">
        <div className="section-kicker">Say hello</div>
        <h2 className="section-title">Get in <em>touch</em></h2>
        <div className="contact-grid">
          <div className="contact-cards">
            {[
              { icon:'fa-location-dot', title:'Address',   lines:['Kacyiru, below US Embassy, Stone Road','KG 684 St, Irebe Compound'] },
              { icon:'fa-clock',        title:'Hours',     lines:['Mon–Fri: 7am – 9pm','Sat–Sun: 8am – 10pm'] },
              { icon:'fa-envelope',     title:'Email',     lines:['cafemuki684@gmail.com'] },
              { icon:'fa-phone',        title:'Phone',     lines:['0782 341 304','0788 546 357'] },
            ].map(c => (
              <div key={c.icon} className="contact-card">
                <i className={`fa-solid ${c.icon} contact-card-icon`} />
                <div><h4>{c.title}</h4>{c.lines.map(l => <p key={l}>{l}</p>)}</div>
              </div>
            ))}
          </div>

          <div className="form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input name="name" required placeholder="Jane Smith" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input name="email" type="email" required placeholder="jane@example.com" />
                </div>
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea name="message" required rows={5} placeholder="Ask us anything..." />
              </div>
              <button type="submit" className="btn btn-primary form-submit" disabled={loading}>
                {loading
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Sending…</>
                  : 'Send message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   FOOTER
================================================================ */
function Footer() {
  return (
    <footer>
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <img src="/logos/logo_06_gold_on_darkgreen.png" alt="Muki 684 Coffee" className="footer-logo-img" />
            <p>Good coffee, great food and better conversations in Kacyiru, Kigali. Come as you are.</p>
            <div className="social-links">
              <a href="https://www.instagram.com/muki684cafe" target="_blank" rel="noreferrer" className="social-link">
                <i className="fa-brands fa-instagram" />
              </a>
              {[
                'fa-brands fa-facebook',
                'fa-brands fa-x-twitter',
                'fa-brands fa-tiktok',
              ].map(i => (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a key={i} href="#social" className="social-link"
                  onClick={e => e.preventDefault()}>
                  <i className={i} />
                </a>
              ))}
            </div>
          </div>

          <div className="footer-col">
            <h4>Visit</h4>
            <ul>
              {['About','Menu','Reviews','Reservations','Contact'].map(l => (
                <li key={l}><a href={`#${l.toLowerCase()}`}>{l}</a></li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <h4>Hours</h4>
            <ul>
              <li><a href="#contact">Mon–Fri 7am–9pm</a></li>
              <li><a href="#contact">Sat–Sun 8am–10pm</a></li>
              <li><a href="#contact">Bank holidays 9am–6pm</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>More</h4>
            <ul>
              <li><a href="/menu">Gift cards</a></li>
              <li><a href="/menu">Catering</a></li>
              <li><a href="/admin">Staff login</a></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Muki 684 Coffee. All rights reserved.</p>
          <p>Made with <span>♥</span> in Kigali</p>
        </div>
      </div>
    </footer>
  );
}

/* ================================================================
   TOAST & BACK-TO-TOP
================================================================ */
function ToastContainer({ toasts }) {
  return (
    <div id="toast">
      {toasts.map(t => <div key={t.id} className={`toast-msg ${t.type}`}>{t.msg}</div>)}
    </div>
  );
}

function BackTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const h = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <button id="back-top" className={visible ? 'visible' : ''}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
      <i className="fa-solid fa-arrow-up" />
    </button>
  );
}

/* ================================================================
   HOME
================================================================ */
export default function Home() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  // Cart state
  const [cartItems, setCartItems]     = useState([]);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const addItem = useCallback((item) => {
    setCartItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...item, qty: 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((id, delta) => {
    setCartItems(prev =>
      prev
        .map(i => i.id === id ? { ...i, qty: i.qty + delta } : i)
        .filter(i => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback((id) => {
    setCartItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const cartTotal = cartItems.reduce((s, i) => s + parseFloat(i.price) * i.qty, 0);
  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);

  const cartCtxVal = {
    items: cartItems, addItem, updateQty, removeItem, clearCart,
    total: cartTotal, count: cartCount,
    drawerOpen, setDrawerOpen, checkoutOpen, setCheckoutOpen,
  };

  return (
    <ToastCtx.Provider value={addToast}>
      <CartCtx.Provider value={cartCtxVal}>
        <Navbar />
        <main>
          <Hero />
          <Gallery />
          <Menu />
          <About />
          <Reviews />
          <Reservations />
          <Contact />
        </main>
        <Footer />
        <CartDrawer />
        <CheckoutModal />
        <ToastContainer toasts={toasts} />
        <BackTop />
      </CartCtx.Provider>
    </ToastCtx.Provider>
  );
}
